/**
 * ============================================================================
 * DualSignPanel — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Purpose: Render the dual-signature gate for CRITICAL-tier deviation closures.
 *          DEV-005 requires 2 distinct signers; this panel surfaces:
 *            - whether dual-sign is required (depends on classification)
 *            - the primary signature id (if any)
 *            - the secondary signature id (if any)
 *            - whether the gate is satisfied (badge color codes)
 *
 *          The panel is NON-INTERACTIVE in this render — the actual signing
 *          UI is invoked via the close mutation. This panel just visualises
 *          the current state of the gate from the server detail payload.
 *
 * URS rows: DEV-005 (Critical-tier dual-sign).
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './DeviationDetail.module.css';
import type { DeviationClassification } from '../DeviationTriage/useDeviationsQuery';
import type { DualSignState } from './useDeviationDetailQuery';

export interface DualSignPanelProps {
  classification: DeviationClassification;
  dualSign: DualSignState;
  closedAtUtc: string | null;
}

export function DualSignPanel({
  classification,
  dualSign,
  closedAtUtc,
}: DualSignPanelProps) {
  const { t } = useTranslation();
  const isCritical = classification === 'CRITICAL';

  // Badge logic: required + satisfied = green; required + not satisfied = amber;
  // not required = neutral.
  const badge = !dualSign.required
    ? {
        cls: styles.dualSignBadgeNa,
        text: t('deviation.dualSign.notRequired', {
          defaultValue: 'Not required',
        }),
      }
    : dualSign.satisfied
      ? {
          cls: styles.dualSignBadgeSatisfied,
          text: t('deviation.dualSign.satisfied', {
            defaultValue: 'Satisfied',
          }),
        }
      : {
          cls: styles.dualSignBadgePending,
          text: t('deviation.dualSign.pending', {
            defaultValue: 'Pending',
          }),
        };

  return (
    <section
      className={`${styles.dualSignCard} ${isCritical ? styles.dualSignCriticalAccent : ''}`}
      aria-labelledby="dual-sign-title"
    >
      <header
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
      >
        <h3 id="dual-sign-title" className={styles.panelTitle}>
          {t('deviation.dualSign.title', {
            defaultValue: 'Closure signatures',
          })}
        </h3>
        <span className={`${styles.dualSignBadge} ${badge.cls}`}>{badge.text}</span>
      </header>
      <p className={styles.subtitle} style={{ margin: 0 }}>
        {isCritical
          ? t('deviation.dualSign.criticalDesc', {
              defaultValue:
                'DEV-005: Critical-tier deviations require two distinct QA signatures to close. The primary and secondary signers must differ.',
            })
          : t('deviation.dualSign.standardDesc', {
              defaultValue:
                'Major and Minor deviations close with a single primary signature.',
            })}
      </p>
      <div className={styles.signSlots}>
        <div
          className={`${styles.signSlot} ${dualSign.primarySignatureId ? styles.signSlotFilled : ''}`}
        >
          <span className={styles.signSlotLabel}>
            {t('deviation.dualSign.primary', {
              defaultValue: 'Primary signer',
            })}
          </span>
          {dualSign.primarySignatureId ? (
            <span className={styles.signSlotValue}>
              {dualSign.primarySignatureId}
            </span>
          ) : (
            <span className={styles.signSlotMissing}>
              {t('deviation.dualSign.empty', {
                defaultValue: 'Not yet signed',
              })}
            </span>
          )}
        </div>
        {(isCritical || dualSign.secondarySignatureId) && (
          <div
            className={`${styles.signSlot} ${dualSign.secondarySignatureId ? styles.signSlotFilled : ''}`}
          >
            <span className={styles.signSlotLabel}>
              {t('deviation.dualSign.secondary', {
                defaultValue: 'Secondary signer (DEV-005)',
              })}
            </span>
            {dualSign.secondarySignatureId ? (
              <span className={styles.signSlotValue}>
                {dualSign.secondarySignatureId}
              </span>
            ) : (
              <span className={styles.signSlotMissing}>
                {t('deviation.dualSign.empty', {
                  defaultValue: 'Not yet signed',
                })}
              </span>
            )}
          </div>
        )}
      </div>
      {closedAtUtc && (
        <p className={styles.timelineMeta} style={{ margin: 0 }}>
          {t('deviation.dualSign.closedAt', {
            defaultValue: 'Closed {{at}}',
            at: new Date(closedAtUtc).toLocaleString(),
          })}
        </p>
      )}
    </section>
  );
}
